// client/src/pages/ProjectsListPage.js

// Organisation-level projects list — the home screen for an authenticated user.
// Shows all projects in the org as a sortable list with headline stats.
// Clicking a row navigates to /project/:id/scorecard.
// Expanding a project row shows its campaigns with Live/Paused/Archived status controls.

// UPDATED Jul 2026: Campaign status controls — Live / Paused / Archived per campaign
// UPDATED Jul 27 2026: Added organization name to page heading. Fetches from GET /api/org/current
// UPDATED Jul 28 2026: Removed "+ Add project" CTA from header. Replaced rocket emoji with Tabler icon.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Load Tabler icons
if (typeof document !== 'undefined') {
  if (!document.querySelector('link[href*="tabler-icons"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css';
    document.head.appendChild(link);
  }
}

const C = {
  jet:      '#39393a',
  blue:     '#048ba8',
  sienna:   '#e76f51',
  pine:     '#01796f',
  grey:     '#6b7280',
  border:   '#e5e7eb',
  lightbg:  '#f9fafb',
  green_tx: '#01796f',
  amber_tx: '#b45309',
  red_tx:   '#b91c1c',
  green_bg: '#e0f5f3',
  amber_bg: '#fef3c7',
  red_bg:   '#fee2e2',
};

const ENQ_LABEL = {
  not_started:        { label: 'Not started',       color: '#9ca3af' },
  discovery_pending:  { label: 'Discovery pending', color: C.amber_tx },
  discovery_running:  { label: 'Running…',          color: C.blue },
  discovery_complete: { label: 'Ready',             color: C.pine },
  discovery_failed:   { label: 'Failed',            color: C.red_tx },
  entities_confirmed: { label: 'Confirmed',         color: C.green_tx },
};

const STATUS_CONFIG = {
  LIVE:     { label: 'Live',     bg: '#E0F5F3', text: '#01796f', dot: '#01796f' },
  PAUSED:   { label: 'Paused',   bg: '#FEF3C7', text: '#b45309', dot: '#f59e0b' },
  ARCHIVED: { label: 'Archived', bg: '#F3F4F6', text: '#6b7280', dot: '#9ca3af' },
};

const CAMP_GRID = '1fr 120px 140px 180px';
const CAMP_INDENT = '48px';

function enqBadge(status) {
  const s = ENQ_LABEL[status] || { label: status || '—', color: C.grey };
  return (
    <span style={{
      fontSize: '11px', fontWeight: '600', color: s.color,
      backgroundColor: s.color + '18', padding: '2px 8px',
      borderRadius: '9999px', whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.LIVE;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      fontSize: '11px', fontWeight: '600', color: cfg.text,
      backgroundColor: cfg.bg, padding: '2px 8px',
      borderRadius: '9999px', whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        backgroundColor: cfg.dot, flexShrink: 0,
      }} />
      {cfg.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function StatusToggle({ current, onSelect, updating }) {
  const statuses = ['LIVE', 'PAUSED', 'ARCHIVED'];
  return (
    <div
      style={{
        display: 'inline-flex', borderRadius: '6px',
        border: `1px solid ${C.border}`, overflow: 'hidden',
        opacity: updating ? 0.5 : 1, transition: 'opacity 0.15s',
      }}
      onClick={e => e.stopPropagation()}
    >
      {statuses.map((s, i) => {
        const isActive = (current || 'LIVE') === s;
        const cfg = STATUS_CONFIG[s];
        return (
          <button
            key={s}
            disabled={updating}
            onClick={() => !isActive && onSelect(s)}
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: '600',
              border: 'none',
              borderRight: i < statuses.length - 1 ? `1px solid ${C.border}` : 'none',
              backgroundColor: isActive ? cfg.bg : 'white',
              color: isActive ? cfg.text : C.grey,
              cursor: isActive || updating ? 'default' : 'pointer',
              transition: 'all 0.15s',
              fontFamily: "'Work Sans', sans-serif",
            }}
          >
            {cfg.label}
          </button>
        );
      })}
    </div>
  );
}

function CampaignRow({ campaign, apiBaseUrl, userId, onStatusChange, isLast }) {
  const navigate   = useNavigate();
  const [updating, setUpdating] = useState(false);
  const [hovered,  setHovered]  = useState(false);

  const currentStatus = campaign.campaign_status ||
    (campaign.archived ? 'ARCHIVED' : 'LIVE');

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const res = await fetch(`${apiBaseUrl}/campaigns/${campaign.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      onStatusChange(campaign.id, newStatus);
    } catch (err) {
      alert(`Could not update status: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: CAMP_GRID,
        alignItems: 'center',
        padding: `10px 20px 10px ${CAMP_INDENT}`,
        borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
        backgroundColor: hovered ? '#f0f9fb' : '#fafbfc',
        transition: 'background-color 0.1s',
        gap: '12px',
      }}
    >
      <button
        onClick={() => navigate(`/campaign/${campaign.id}/summary`)}
        style={{
          textAlign: 'left', background: 'none', border: 'none',
          cursor: 'pointer', padding: 0,
          fontSize: '13px', fontWeight: '500',
          color: hovered ? C.blue : C.jet,
          transition: 'color 0.1s',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {campaign.name}
      </button>
      <div><StatusBadge status={currentStatus} /></div>
      <div style={{ fontSize: '12px', color: C.grey }}>
        {campaign.last_llm_audit_run ? formatDate(campaign.last_llm_audit_run) : 'Never run'}
      </div>
      <div style={{
        opacity: hovered || currentStatus !== 'LIVE' ? 1 : 0,
        transition: 'opacity 0.15s',
      }}>
        <StatusToggle
          current={currentStatus}
          onSelect={handleStatusChange}
          updating={updating}
        />
      </div>
    </div>
  );
}

function ProjectsListPage({ apiBaseUrl, userId }) {
  const navigate = useNavigate();
  const [projects,     setProjects]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [hovered,      setHovered]      = useState(null);
  const [expandedIds,  setExpandedIds]  = useState(new Set());
  const [campaignMap,  setCampaignMap]  = useState({});
  const [loadingCamps, setLoadingCamps] = useState(new Set());
  const [showArchived, setShowArchived] = useState(new Set());
  const [orgName,      setOrgName]      = useState(null);

  useEffect(() => {
    if (!apiBaseUrl || !userId) return;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch org name
        const orgRes = await fetch(`${apiBaseUrl}/org/current`, {
          headers: { 'x-user-id': userId },
        });
        if (orgRes.ok) {
          const orgData = await orgRes.json();
          setOrgName(orgData.name || 'Organization');
        }

        // Fetch projects
        const res = await fetch(`${apiBaseUrl}/projects`, {
          headers: { 'x-user-id': userId },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'Failed to load projects');

        const sorted = (json.projects || []).sort((a, b) => {
          if ((b.campaign_count ?? 0) !== (a.campaign_count ?? 0))
            return (b.campaign_count ?? 0) - (a.campaign_count ?? 0);
          return new Date(b.created_at) - new Date(a.created_at);
        });

        setProjects(sorted);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [apiBaseUrl, userId]);

  const loadCampaigns = useCallback(async (projectId) => {
    if (campaignMap[projectId]) return;

    setLoadingCamps(prev => new Set([...prev, projectId]));
    try {
      const res = await fetch(`${apiBaseUrl}/campaigns?project_id=${projectId}`, {
        headers: { 'x-user-id': userId },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const campaigns = Array.isArray(data) ? data : (data.campaigns || []);

      setCampaignMap(prev => ({ ...prev, [projectId]: campaigns }));
    } catch (err) {
      setCampaignMap(prev => ({ ...prev, [projectId]: [] }));
    } finally {
      setLoadingCamps(prev => { const n = new Set(prev); n.delete(projectId); return n; });
    }
  }, [apiBaseUrl, userId, campaignMap]);

  const toggleExpand = (e, projectId) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) { next.delete(projectId); }
      else { next.add(projectId); loadCampaigns(projectId); }
      return next;
    });
  };

  const handleStatusChange = (projectId, campaignId, newStatus) => {
    setCampaignMap(prev => ({
      ...prev,
      [projectId]: (prev[projectId] || []).map(c =>
        String(c.id) === String(campaignId)
          ? { ...c, campaign_status: newStatus, archived: newStatus === 'ARCHIVED' }
          : c
      ),
    }));
  };

  const toggleShowArchived = (e, projectId) => {
    e.stopPropagation();
    setShowArchived(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId); else next.add(projectId);
      return next;
    });
  };

  return (
    <div style={{
      padding: '32px', maxWidth: '960px',
      fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: '32px',
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '28px', fontWeight: '800', color: C.jet, margin: 0, marginBottom: '20px' }}>
            Dashboard
          </h1>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              backgroundColor: C.lightbg, border: `2px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, fontSize: '16px', fontWeight: '700', color: C.blue,
            }}>
              {orgName ? orgName.charAt(0).toUpperCase() : '?'}
            </div>
            <div>
              <div style={{ fontSize: '14px', color: C.grey }}>
                Organization: <span style={{ fontWeight: '600', color: C.jet }}>{orgName || 'My Organization'}</span>
              </div>
              <div style={{ fontSize: '12px', color: C.grey, marginTop: '4px' }}>
                {loading ? 'Loading…' : `${projects.length} project${projects.length !== 1 ? 's' : ''}`}
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', color: C.grey, padding: '48px' }}>
          <div style={{
            width: '28px', height: '28px', border: `3px solid ${C.border}`,
            borderTop: `3px solid ${C.blue}`, borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 12px',
          }} />
          Loading projects…
        </div>
      )}

      {!loading && error && (
        <div style={{ color: C.red_tx, padding: '24px', textAlign: 'center' }}>{error}</div>
      )}

      {!loading && !error && projects.length === 0 && (
        <div style={{
          backgroundColor: 'white', border: `1px solid ${C.border}`,
          borderRadius: '12px', padding: '48px', textAlign: 'center',
        }}>
          <i className="ti ti-rocket" style={{ fontSize: '32px', color: C.grey, marginBottom: '12px', display: 'block' }} />
          <div style={{ fontSize: '16px', fontWeight: '700', color: C.jet, marginBottom: '8px' }}>No projects yet</div>
          <div style={{ fontSize: '14px', color: C.grey, marginBottom: '24px' }}>
            Add your first client project to start tracking AI visibility.
          </div>
          <button
            onClick={() => navigate('/project/new')}
            style={{
              backgroundColor: C.blue, color: 'white', border: 'none',
              borderRadius: '8px', padding: '12px 24px',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              fontFamily: "'Work Sans', sans-serif",
            }}
          >
            + Add project
          </button>
        </div>
      )}

      {!loading && !error && projects.length > 0 && (
        <div style={{
          backgroundColor: 'white', border: `1px solid ${C.border}`,
          borderRadius: '12px', overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '28px 2fr 1.5fr 80px 1fr 1fr',
            padding: '10px 20px',
            backgroundColor: C.lightbg,
            borderBottom: `1px solid ${C.border}`,
            gap: '12px',
          }}>
            <div />
            {['Project', 'Domain', 'Campaigns', 'ENQ Status', 'Created'].map(h => (
              <div key={h} style={{
                fontSize: '11px', fontWeight: '700', color: C.grey,
                textTransform: 'uppercase', letterSpacing: '0.6px',
              }}>
                {h}
              </div>
            ))}
          </div>

          {projects.map((project, i) => {
            const isHovered  = hovered === project.id;
            const isExpanded = expandedIds.has(project.id);
            const isLast     = i === projects.length - 1;
            const campaigns  = campaignMap[project.id] || [];
            const isLoadingC = loadingCamps.has(project.id);
            const showArch   = showArchived.has(project.id);

            const visibleCampaigns = campaigns.filter(c =>
              showArch || (c.campaign_status || (c.archived ? 'ARCHIVED' : 'LIVE')) !== 'ARCHIVED'
            );

            const archivedCount = campaigns.filter(c =>
              (c.campaign_status || (c.archived ? 'ARCHIVED' : 'LIVE')) === 'ARCHIVED'
            ).length;

            return (
              <div key={project.id}>
                <div
                  onClick={() => navigate(`/project/${project.id}/scorecard`)}
                  onMouseEnter={() => setHovered(project.id)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '28px 2fr 1.5fr 80px 1fr 1fr',
                    padding: '14px 20px',
                    borderBottom: `1px solid ${C.border}`,
                    backgroundColor: isExpanded ? '#EBF7FA' : isHovered ? C.lightbg : 'white',
                    cursor: 'pointer',
                    transition: 'background-color 0.1s',
                    alignItems: 'center',
                    gap: '12px',
                    borderLeft: isExpanded ? `3px solid ${C.blue}` : '3px solid transparent',
                  }}
                >
                  <button
                    onClick={e => toggleExpand(e, project.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '2px', color: isExpanded ? C.blue : C.grey,
                      fontSize: '12px', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', borderRadius: '4px',
                      fontWeight: isExpanded ? '700' : '400',
                    }}
                    aria-label={isExpanded ? 'Collapse' : 'Expand campaigns'}
                  >
                    {isExpanded ? '▾' : '▸'}
                  </button>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {project.domain && (
                      <span style={{
                        width: 24, height: 24, borderRadius: '50%',
                        backgroundColor: C.border, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <img
                          src={`https://www.google.com/s2/favicons?domain=${project.domain}&sz=32`}
                          alt="" width={14} height={14} style={{ borderRadius: 2 }}
                          onError={e => { e.target.parentElement.style.display = 'none'; }}
                        />
                      </span>
                    )}
                    <span style={{
                      fontSize: '14px', fontWeight: '600',
                      color: isExpanded ? C.blue : isHovered ? C.blue : C.jet,
                      transition: 'color 0.1s',
                    }}>
                      {project.name}
                    </span>
                  </div>

                  <div style={{ fontSize: '13px', color: C.grey, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.domain || '—'}</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: C.jet }}>{project.campaign_count ?? 0}</div>
                  <div>{enqBadge(project.enq_status)}</div>
                  <div style={{ fontSize: '13px', color: C.grey }}>{formatDate(project.created_at)}</div>
                </div>

                {isExpanded && (
                  <div style={{
                    borderLeft: `3px solid ${C.blue}`,
                    borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
                    backgroundColor: '#f7fcfd',
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: CAMP_GRID,
                      padding: `8px 20px 8px ${CAMP_INDENT}`,
                      borderBottom: `1px solid ${C.border}`,
                      backgroundColor: '#edf6f9',
                      gap: '12px',
                    }}>
                      {['Campaign', 'Status', 'Last run', ''].map(h => (
                        <div key={h} style={{
                          fontSize: '10px', fontWeight: '700',
                          color: C.blue, textTransform: 'uppercase', letterSpacing: '0.6px',
                        }}>
                          {h}
                        </div>
                      ))}
                    </div>

                    {isLoadingC && (
                      <div style={{ padding: `14px 20px 14px ${CAMP_INDENT}`, fontSize: '13px', color: C.grey }}>
                        Loading campaigns…
                      </div>
                    )}

                    {!isLoadingC && campaigns.length === 0 && (
                      <div style={{ padding: `14px 20px 14px ${CAMP_INDENT}`, fontSize: '13px', color: C.grey, fontStyle: 'italic' }}>
                        No campaigns yet for this project.
                      </div>
                    )}

                    {!isLoadingC && visibleCampaigns.map((campaign, ci) => (
                      <CampaignRow
                        key={campaign.id}
                        campaign={campaign}
                        apiBaseUrl={apiBaseUrl}
                        userId={userId}
                        isLast={ci === visibleCampaigns.length - 1 && archivedCount === 0}
                        onStatusChange={(cid, status) => handleStatusChange(project.id, cid, status)}
                      />
                    ))}

                    {!isLoadingC && archivedCount > 0 && (
                      <div style={{ padding: `8px 20px 8px ${CAMP_INDENT}`, borderTop: `1px solid ${C.border}` }}>
                        <button
                          onClick={e => toggleShowArchived(e, project.id)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: '12px', color: C.grey, padding: 0,
                            fontFamily: "'Work Sans', sans-serif",
                          }}
                        >
                          {showArch
                            ? `Hide archived (${archivedCount})`
                            : `Show ${archivedCount} archived campaign${archivedCount !== 1 ? 's' : ''}`
                          }
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ProjectsListPage;
